import { supabase } from '@/services/supabase';
import { User } from '@supabase/supabase-js';

export interface UserProgress {
  id?: string;
  user_id: string;
  course_id: string;
  progress: Record<string, boolean>; // nodeId -> completed
  last_accessed: string;
  completed_nodes: string[];
  progress_percentage: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Lấy tiến độ học tập của người dùng cho một khóa học cụ thể
 */
export const getUserCourseProgress = async (courseId: string): Promise<UserProgress | null> => {
  try {
    console.log('getUserCourseProgress called for course:', courseId);
    
    // Kiểm tra courseId hợp lệ
    if (!courseId || courseId === '[object Object]' || typeof courseId !== 'string') {
      console.error('Invalid courseId provided:', courseId);
      return null;
    }
    
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      console.log('getUserCourseProgress: No user found');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.user.id)
        .eq('course_id', courseId)
        .single();

      if (error) {
        // Kiểm tra nếu lỗi là do bảng không tồn tại
        if (error.code === '42P01') { // relation does not exist
          console.warn('Bảng user_progress chưa được tạo trong cơ sở dữ liệu');
          return null;
        }
        
        // Kiểm tra nếu lỗi là do không tìm thấy bản ghi
        if (error.code === 'PGRST116') {
          console.log('Không tìm thấy tiến độ cho khóa học này');
          return null;
        }
        
        console.error('Error fetching user progress:', error);
        return null;
      }

      console.log('getUserCourseProgress: Progress data fetched:', data);
      return data as UserProgress;
    } catch (error) {
      // Xử lý lỗi khi bảng không tồn tại
      console.warn('Không thể truy cập bảng user_progress:', error);
      return null;
    }
  } catch (error) {
    console.error('Error in getUserCourseProgress:', error);
    return null;
  }
};

/**
 * Lấy tất cả tiến độ học tập của người dùng
 */
export const getAllUserProgress = async (): Promise<UserProgress[]> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return [];

    const { data, error } = await supabase
      .from('user_progress')
      .select('*')
      .eq('user_id', user.user.id);

    if (error) {
      console.error('Error fetching all user progress:', error);
      return [];
    }

    return data as UserProgress[];
  } catch (error) {
    console.error('Error in getAllUserProgress:', error);
    return [];
  }
};

/**
 * Cập nhật tiến độ học tập của người dùng
 */
export const updateUserProgress = async (
  courseId: string,
  progress: Record<string, boolean>,
  completedNodes: string[] = []
): Promise<UserProgress | null> => {
  try {
    console.log('updateUserProgress called for course:', courseId);
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      console.log('updateUserProgress: No user found');
      return null;
    }

    // Tính toán phần trăm hoàn thành
    const totalNodes = Object.keys(progress).length;
    const completedCount = Object.values(progress).filter(Boolean).length;
    const progressPercentage = totalNodes > 0 ? (completedCount / totalNodes) * 100 : 0;

    try {
      // Kiểm tra xem đã có bản ghi chưa
      const { data: existingProgress, error: checkError } = await supabase
        .from('user_progress')
        .select('id')
        .eq('user_id', user.user.id)
        .eq('course_id', courseId)
        .single();
        
      // Nếu lỗi là do bảng không tồn tại, lưu vào localStorage và trả về null
      if (checkError && checkError.code === '42P01') {
        console.warn('Bảng user_progress chưa được tạo trong cơ sở dữ liệu');
        localStorage.setItem(`course-progress-${courseId}`, JSON.stringify(progress));
        return null;
      }

      let result;

      if (existingProgress) {
        console.log('updateUserProgress: Updating existing progress');
        // Cập nhật bản ghi hiện có
        const { data, error } = await supabase
          .from('user_progress')
          .update({
            progress,
            completed_nodes: completedNodes.length > 0 ? completedNodes : Object.keys(progress).filter(key => progress[key]),
            progress_percentage: progressPercentage,
            last_accessed: new Date().toISOString(),
          })
          .eq('id', existingProgress.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating user progress:', error);
          return null;
        }

        result = data;
      } else {
        console.log('updateUserProgress: Creating new progress');
        // Tạo bản ghi mới
        const { data, error } = await supabase
          .from('user_progress')
          .insert({
            user_id: user.user.id,
            course_id: courseId,
            progress,
            completed_nodes: completedNodes.length > 0 ? completedNodes : Object.keys(progress).filter(key => progress[key]),
            progress_percentage: progressPercentage,
            last_accessed: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating user progress:', error);
          return null;
        }

        result = data;
      }

      console.log('updateUserProgress: Progress updated successfully');
      return result as UserProgress;
    } catch (error) {
      // Xử lý lỗi khi bảng không tồn tại
      console.warn('Không thể truy cập bảng user_progress:', error);
      
      // Lưu vào localStorage như một giải pháp tạm thời
      localStorage.setItem(`course-progress-${courseId}`, JSON.stringify(progress));
      
      return null;
    }
  } catch (error) {
    console.error('Error in updateUserProgress:', error);
    return null;
  }
};

/**
 * Xóa tiến độ học tập của người dùng cho một khóa học
 */
export const deleteUserProgress = async (courseId: string): Promise<boolean> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { error } = await supabase
      .from('user_progress')
      .delete()
      .eq('user_id', user.user.id)
      .eq('course_id', courseId);

    if (error) {
      console.error('Error deleting user progress:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteUserProgress:', error);
    return false;
  }
};

/**
 * Hợp nhất tiến độ học tập từ localStorage với tiến độ trong cơ sở dữ liệu
 */
export const mergeProgressWithLocalStorage = async (courseId: string): Promise<UserProgress | null> => {
  try {
    // Lấy tiến độ từ localStorage
    const localProgressStr = localStorage.getItem(`course-progress-${courseId}`);
    if (!localProgressStr) return null;
    
    const localProgress = JSON.parse(localProgressStr) as Record<string, boolean>;
    
    // Lấy tiến độ từ cơ sở dữ liệu
    const dbProgress = await getUserCourseProgress(courseId);
    
    // Nếu không có tiến độ trong DB, cập nhật với tiến độ từ localStorage
    if (!dbProgress) {
      return await updateUserProgress(courseId, localProgress);
    }
    
    // Hợp nhất tiến độ, ưu tiên giá trị true (đã hoàn thành)
    const mergedProgress = { ...dbProgress.progress };
    
    for (const [nodeId, completed] of Object.entries(localProgress)) {
      // Nếu node đã hoàn thành trong localStorage hoặc DB, đánh dấu là hoàn thành
      mergedProgress[nodeId] = completed || !!mergedProgress[nodeId];
    }
    
    // Cập nhật tiến độ đã hợp nhất
    return await updateUserProgress(courseId, mergedProgress);
  } catch (error) {
    console.error('Error in mergeProgressWithLocalStorage:', error);
    return null;
  }
};

/**
 * Kiểm tra xem người dùng đã hoàn thành khóa học chưa
 */
export const isCourseFulfilledByUser = async (courseId: string, totalNodes: number): Promise<boolean> => {
  try {
    // Kiểm tra tiến độ từ cơ sở dữ liệu
    const progress = await getUserCourseProgress(courseId);
    
    if (progress) {
      const completedCount = Object.values(progress.progress).filter(Boolean).length;
      return completedCount === totalNodes;
    }
    
    // Nếu không tìm thấy trong cơ sở dữ liệu, kiểm tra từ localStorage
    const savedProgress = localStorage.getItem(`course-progress-${courseId}`);
    if (savedProgress) {
      try {
        const parsedProgress = JSON.parse(savedProgress);
        const completedCount = Object.values(parsedProgress).filter(Boolean).length;
        return completedCount === totalNodes;
      } catch (e) {
        console.error('Error parsing saved progress:', e);
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error in isCourseFulfilledByUser:', error);
    
    // Nếu có lỗi, thử kiểm tra từ localStorage
    try {
      const savedProgress = localStorage.getItem(`course-progress-${courseId}`);
      if (savedProgress) {
        const parsedProgress = JSON.parse(savedProgress);
        const completedCount = Object.values(parsedProgress).filter(Boolean).length;
        return completedCount === totalNodes;
      }
    } catch (e) {
      console.error('Error checking localStorage progress:', e);
    }
    
    return false;
  }
}; 